import type { ReactNode } from 'react'
import React from 'react'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import type { CoverageTooltip } from '@flows/index'
import InfoButton from '@ui-kit/components/InfoButton'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { body1, body2, caption, h4 } from '@ui-kit/core/typography'

const styles = tv({
  slots: {
    container: 'flex-col gap-4',
    header: 'flex-row items-end justify-between',
    headerTitleGroup: 'flex-1 flex-col gap-0.5',
    headerTitle: h4(),
    headerSubtitle: body2({ color: 'grey700' }),
    headerDetails: 'flex-row items-center gap-1.5',
    headerDetailsLabel: caption({ color: 'black', className: 'underline' }),
    card: 'flex-col gap-5 overflow-hidden rounded-xl border border-grey-300 py-5',
    divider: 'h-px w-full bg-grey-200',
    itemContent: 'flex-1 flex-col gap-0.5',
    itemTitleRow: 'flex-row items-center gap-1',
    itemTitle: body1({ bold: true }),
    itemDescription: body2({ color: 'grey700' }),
    itemValueContainer: 'max-w-[50%] flex-row items-center gap-1.5',
    itemValueIcon: 'shrink-0',
    itemValue: body2({ className: 'shrink' }),
  },
})

const rowStyles = tv({
  base: 'px-6',
  variants: {
    layout: {
      row: 'flex-row flex-wrap items-center justify-between gap-4',
      column:
        'flex-col gap-4 min-[492px]:flex-row min-[492px]:items-center min-[492px]:justify-between',
    },
  },
  defaultVariants: { layout: 'row' },
})

export interface CoverageBreakdownRow {
  key: string
  title: string
  description?: string
  tooltip?: CoverageTooltip
  rateSelector?: ReactNode
  fixedRateValue?: string
}

interface Props {
  title: string
  subtitle?: string
  showDetailsTitle?: string
  onShowDetails?: () => void
  rows: CoverageBreakdownRow[]
}

const Row = ({ row, isFirst }: { row: CoverageBreakdownRow; isFirst: boolean }) => {
  const {
    divider,
    itemContent,
    itemTitleRow,
    itemTitle,
    itemDescription,
    itemValueContainer,
    itemValueIcon,
    itemValue,
  } = styles()

  return (
    <>
      {isFirst ? null : <View className={divider()} />}
      <View className={rowStyles({ layout: row.rateSelector ? 'column' : 'row' })}>
        <View className={itemContent()}>
          <View className={itemTitleRow()}>
            <Text className={itemTitle()}>{row.title}</Text>
            {row.tooltip ? (
              <InfoButton
                type="info"
                modalTitle={row.tooltip.title}
                modalContent={<Text className={itemDescription()}>{row.tooltip.description}</Text>}
              />
            ) : null}
          </View>
          {row.description ? <Text className={itemDescription()}>{row.description}</Text> : null}
        </View>
        {row.rateSelector}
        {row.fixedRateValue && !row.rateSelector ? (
          <View className={itemValueContainer()}>
            <View className={itemValueIcon()}>
              <PhosphorIcon name="Check" size={24} weight="bold" />
            </View>
            <Text className={itemValue()}>{row.fixedRateValue}</Text>
          </View>
        ) : null}
      </View>
    </>
  )
}

export const CoverageBreakdown = ({
  title,
  subtitle,
  showDetailsTitle,
  onShowDetails,
  rows,
}: Props) => {
  const {
    container,
    header,
    headerTitleGroup,
    headerTitle,
    headerSubtitle,
    headerDetails,
    headerDetailsLabel,
    card,
  } = styles()

  return (
    <View className={container()}>
      <View className={header()}>
        <View className={headerTitleGroup()}>
          <Text className={headerTitle()}>{title}</Text>
          {subtitle ? <Text className={headerSubtitle()}>{subtitle}</Text> : null}
        </View>
        {showDetailsTitle ? (
          <Pressable
            testID="coverage-breakdown-details-button"
            accessibilityRole="button"
            className={headerDetails()}
            onPress={onShowDetails}
          >
            <PhosphorIcon name="Info" size={16} weight="bold" />
            <Text className={headerDetailsLabel()}>{showDetailsTitle}</Text>
          </Pressable>
        ) : null}
      </View>
      {rows.length > 0 && (
        <View className={card()}>
          {rows.map((row, index) => (
            <Row key={row.key} row={row} isFirst={index === 0} />
          ))}
        </View>
      )}
    </View>
  )
}
